import { useMemo } from "react";
import { addDays, format, isToday, startOfDay } from "date-fns";
import { pl } from "date-fns/locale";
import { Exercise } from "../../models/Exercise";
import { ExerciseScheduleList } from "../../models/ExerciseScheduleList";
import { isDateInSaleStepRange } from "../shared/scheduleDate";

const WEEKDAY_KEYS: (keyof ExerciseScheduleList)[] = [
  "Poniedziałek",
  "Wtorek",
  "Środa",
  "Czwartek",
  "Piątek",
  "Sobota",
  "Niedziela",
];

const weekdayKeyForDate = (dateKey: string): keyof ExerciseScheduleList => {
  const name = format(new Date(`${dateKey}T00:00:00`), "EEEE", { locale: pl });
  const capitalized = (name.charAt(0).toUpperCase() + name.slice(1)) as keyof ExerciseScheduleList;
  return WEEKDAY_KEYS.includes(capitalized) ? capitalized : "Poniedziałek";
};

export const exerciseHourKey = (exercise: Exercise): string => {
  const hour = new Date(exercise.startTime).getHours();
  return `${String(hour).padStart(2, "0")}:00`;
};

export default function useFitnessWeekView(
  weekStart: Date,
  exercisesList: ExerciseScheduleList,
  minDate?: Date,
  maxDate?: Date,
) {
  const columns = useMemo(
    () => Array.from({ length: 7 }, (_, index) => format(addDays(startOfDay(weekStart), index), "yyyy-MM-dd")),
    [weekStart],
  );

  const isColumnInRange = (dayKey: string) =>
    isDateInSaleStepRange(new Date(`${dayKey}T00:00:00`), minDate, maxDate);

  const exercisesForColumn = (dayKey: string): Exercise[] => exercisesList[weekdayKeyForDate(dayKey)] ?? [];

  const allHours = useMemo(() => {
    const hours = new Set<string>();
    columns.forEach((dayKey) => {
      if (!isDateInSaleStepRange(new Date(`${dayKey}T00:00:00`), minDate, maxDate)) return;
      exercisesForColumn(dayKey).forEach((exercise) => hours.add(exerciseHourKey(exercise)));
    });
    return Array.from(hours).sort();
    // exercisesForColumn czyta aktualną listę z tego renderu
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, exercisesList, minDate, maxDate]);

  const isPastHour = (dayKey: string, hour: string) => {
    const day = new Date(`${dayKey}T00:00:00`);
    if (!isToday(day)) return false;
    const exercises = exercisesForColumn(dayKey).filter((exercise) => exerciseHourKey(exercise) === hour);
    if (exercises.length === 0) return false;
    const now = Date.now();
    return exercises.every((exercise) => exercise.closeTime <= now);
  };

  const isEmpty = columns.every((dayKey) => !isColumnInRange(dayKey) || exercisesForColumn(dayKey).length === 0);

  return {
    columns,
    allHours,
    isPastHour,
    isColumnInRange,
    exercisesForColumn,
    isEmpty,
  };
}
